import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import SEO from "@/components/SEO";
import Footer from "@/components/Footer";
import { Radio, Wifi, Clock, Activity, Signal, Antenna, Sun, AlertTriangle, BarChart2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SHACK_PHOTO = "https://customer-assets.emergentagent.com/job_morse-chronicles/artifacts/kxi9gosn_AD0K%20DXLab%20FT-8.jpg";

const COND_STYLE = {
  "Good":        { dot: "bg-green-400",  text: "text-green-400",    label: "GOOD"   },
  "Fair":        { dot: "bg-yellow-400", text: "text-yellow-400",   label: "FAIR"   },
  "Poor":        { dot: "bg-red-500",    text: "text-red-400",      label: "POOR"   },
  "Band Closed": { dot: "bg-slate-600",  text: "text-[#F5F0E8]/25", label: "CLOSED" },
  "E-Skip Open": { dot: "bg-cyan-400",   text: "text-cyan-400",     label: "E-SKIP" },
};
function cs(cond) {
  return COND_STYLE[cond] || { dot: "bg-slate-600", text: "text-[#F5F0E8]/30", label: cond || "N/A" };
}

// ── SVG Gauge ──────────────────────────────────────────────────────────────
function SolarGauge({ value, min, max, zones, label, unit }) {
  const cx = 80, cy = 82, r = 55;
  const START = 135, SWEEP = 270;
  const toR = d => (d * Math.PI) / 180;
  const pt = (deg, radius) => ({
    x: cx + radius * Math.cos(toR(deg)),
    y: cy + radius * Math.sin(toR(deg)),
  });
  const arc = (a1, a2, radius) => {
    const p1 = pt(a1, radius), p2 = pt(a2, radius);
    return `M${p1.x.toFixed(1)},${p1.y.toFixed(1)} A${radius},${radius} 0 ${a2-a1>180?1:0} 1 ${p2.x.toFixed(1)},${p2.y.toFixed(1)}`;
  };
  const raw = parseFloat(value) || 0;
  const clamped = Math.max(min, Math.min(max, raw));
  const frac = (clamped - min) / (max - min);
  const valAngle = START + frac * SWEEP;
  const zone = [...zones].reverse().find(z => clamped >= z.from) || zones[0];
  const color = zone?.color || "#4ade80";
  const tip = pt(valAngle, r - 8);
  const b1  = pt(valAngle + 90, 4);
  const b2  = pt(valAngle - 90, 4);

  return (
    <svg viewBox="0 0 160 132" className="w-full">
      <path d={arc(START, START + SWEEP, r)} fill="none" stroke="#0f172a" strokeWidth="9" strokeLinecap="round" />
      {zones.map((z, i) => {
        const a1 = START + ((z.from - min) / (max - min)) * SWEEP;
        const a2 = START + ((Math.min(z.to, max) - min) / (max - min)) * SWEEP;
        return <path key={i} d={arc(a1, a2, r)} fill="none" stroke={z.color}
          strokeWidth="8" opacity="0.2" strokeLinecap={i === 0 ? "round" : "butt"} />;
      })}
      {frac > 0.01 && (
        <path d={arc(START, valAngle, r)} fill="none" stroke={color} strokeWidth="8" strokeLinecap="round" />
      )}
      <polygon points={`${tip.x.toFixed(1)},${tip.y.toFixed(1)} ${b1.x.toFixed(1)},${b1.y.toFixed(1)} ${b2.x.toFixed(1)},${b2.y.toFixed(1)}`}
        fill={color} opacity="0.95" />
      <circle cx={cx} cy={cy} r="5" fill="#1e293b" stroke={color} strokeWidth="1.5" />
      <text x={cx} y={cy + 16} textAnchor="middle" fill="white" fontSize="22" fontWeight="bold" fontFamily="monospace">
        {value ?? "--"}
      </text>
      <text x={cx} y={cy + 29} textAnchor="middle" fill="#475569" fontSize="8" fontFamily="monospace">{unit}</text>
      <text x="20" y="129" textAnchor="middle" fill="#1e293b" fontSize="8" fontFamily="monospace">{min}</text>
      <text x="140" y="129" textAnchor="middle" fill="#1e293b" fontSize="8" fontFamily="monospace">{max}</text>
      <text x={cx} y="129" textAnchor="middle" fill="#C5A059" fontSize="9" fontFamily="monospace" letterSpacing="1">{label}</text>
    </svg>
  );
}

// ── 27-Day Forecast Bar Chart ───────────────────────────────────────────────
function ForecastChart({ data }) {
  if (!data || data.length === 0) return null;
  const sfis = data.map(d => d.sfi);
  const lo = Math.min(...sfis) - 5, hi = Math.max(...sfis) + 5;
  const H = 56, W = 270;
  const bw = W / data.length;
  const kCol = k => k <= 2 ? "#4ade80" : k <= 4 ? "#facc15" : k <= 6 ? "#fb923c" : "#ef4444";
  const today = new Date().toISOString().slice(0, 10);
  return (
    <div className="bg-[#0A1628]/60 border border-[#C5A059]/10 p-3 h-full">
      <p className="text-[#C5A059]/50 text-xs font-mono uppercase tracking-widest mb-2">27-Day SFI Forecast</p>
      <svg viewBox={`0 0 ${W} ${H + 18}`} className="w-full">
        {data.map((d, i) => {
          const bh = ((d.sfi - lo) / (hi - lo)) * H;
          const x = i * bw;
          const isToday = d.date === today;
          return (
            <g key={d.date}>
              <rect x={x + 0.5} y={H - bh} width={bw - 1} height={bh}
                fill={kCol(d.kp)} opacity={isToday ? 1 : 0.65} />
              {isToday && <rect x={x} y={0} width={bw} height={H} fill="white" opacity="0.05" />}
              {i % 7 === 0 && (
                <text x={x + bw / 2} y={H + 14} textAnchor="middle"
                  fill="#334155" fontSize="7" fontFamily="monospace">
                  {d.date.slice(5)}
                </text>
              )}
            </g>
          );
        })}
      </svg>
      <div className="flex flex-wrap gap-3 mt-1 text-xs font-mono text-[#F5F0E8]/30">
        <span>SFI {lo+5}–{hi-5}</span>
        {[["#4ade80","K 0-2"],["#facc15","K 3-4"],["#fb923c","K 5-6"],["#ef4444","K 7+"]].map(([c,l]) => (
          <span key={l} className="flex items-center gap-1">
            <span className="w-2 h-2 inline-block rounded-sm" style={{background:c}} />{l}
          </span>
        ))}
      </div>
    </div>
  );
}

export default function RadioPage() {
  const [status, setStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(true);
  const [lastFetched, setLastFetched] = useState(null);
  const [prop, setProp] = useState(null);
  const [propLoading, setPropLoading] = useState(true);
  const [widgetHtml, setWidgetHtml] = useState(null);

  useEffect(() => {
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Station ADØK — Amateur Radio — Donald S. Inbody",
      "description": "Live station telemetry for amateur radio station ADØK operated by Donald S. Inbody. Active on HF bands 160m–6m. DXCC Award holder with 300+ confirmed entities.",
      "url": "https://inbody.net/radio",
      "mainEntity": {
        "@type": "RadioStation",
        "name": "Station ADØK",
        "callSign": "ADØK",
        "description": "HF amateur radio station active on 160m–6m, operated by Donald S. Inbody. DXCC Award holder. Member of Central Texas DX and Contesting Club.",
        "operator": { "@type": "Person", "name": "Donald S. Inbody", "url": "https://inbody.net" }
      }
    });
    document.head.appendChild(script);
    return () => document.head.removeChild(script);
  }, []);

  useEffect(() => {
    fetch(`${API}/widget`)
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data?.html_content) setWidgetHtml(data.html_content); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const fetchStatus = () => {
      fetch(`${API}/station-status`)
        .then(r => r.json())
        .then(data => { setStatus(data); setLastFetched(new Date()); })
        .catch(() => {})
        .finally(() => setStatusLoading(false));
    };
    fetchStatus();
    const interval = setInterval(fetchStatus, 120000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const fetchProp = () => {
      fetch(`${API}/propagation`)
        .then(r => r.json())
        .then(data => setProp(data))
        .catch(() => {})
        .finally(() => setPropLoading(false));
    };
    fetchProp();
    const interval = setInterval(fetchProp, 900000);
    return () => clearInterval(interval);
  }, []);

  const isOnline = (() => {
    if (!status?.on_air) return false;
    if (status?.updated_at) {
      const ageMs = Date.now() - new Date(status.updated_at).getTime();
      if (ageMs > 15 * 60 * 1000) return false; // stale > 15 min → offline
    }
    return true;
  })();

  return (
    <div className="bg-[#F5F0E8]">
      <SEO
        title="ADØK — Amateur Radio Station"
        description="Donald Stephen Inbody, callsign ADØK — licensed amateur radio operator since 1976. Active on all HF bands and modes. DXCC Award holder (300+ entities). Member, Central Texas DX and Contesting Club."
        canonical="https://inbody.net/ad0k"
        keywords="ADØK, amateur radio, HF bands, DXCC Award, Central Texas DX Contesting Club, Donald Stephen Inbody radio"
      />
      <Navbar />
      <section
        data-testid="shack-photo-section"
        className="relative min-h-[65vh] flex items-end overflow-hidden"
      >
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${SHACK_PHOTO})` }}
                    role="img"
                    aria-label="Donald S. Inbody operating amateur radio station ADØK — HF transceiver and DXLab Suite FT-8 digital mode setup"
        />
        {/* Dark gradient at bottom for text readability */}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A1628]/90 via-[#0A1628]/40 to-transparent" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 pb-16 pt-32 w-full">
          <div className="flex items-end justify-between gap-6 flex-wrap">
            <div>
              <p className="overline text-[#C5A059] mb-3">Amateur Radio Station</p>
              <h1 data-testid="radio-page-title" className="font-serif text-5xl font-black text-[#F5F0E8] mb-3">
                Station ADØK
              </h1>
              <p className="text-[#F5F0E8]/75 max-w-xl leading-relaxed">
                Licensed since 1976. Active on all HF bands and modes — DXCC 300+, ragchewer, contester, and lifelong student of the ionosphere.
              </p>
            </div>
            <div className="flex items-center gap-2 mb-1">
              <div className={`w-2.5 h-2.5 rounded-full ${isOnline ? 'bg-green-400 animate-pulse' : 'bg-white/30'}`} />
              <span className={`text-sm font-mono font-medium ${isOnline ? 'text-green-400' : 'text-white/50'}`}>
                {isOnline ? 'Station On Air' : 'Station Offline'}
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Main content: 2-column layout */}
      <section data-testid="about-radio-section" className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">

            {/* ── LEFT: Story + Shack ── */}
            <div className="space-y-12">

              {/* The Story */}
              <div>
                <p className="overline mb-3">The Story</p>
                <h2 className="font-serif text-3xl font-bold text-[#0A1628] mb-6">
                  Communications<br />&amp; Ragchewing
                </h2>
                <div className="space-y-4 text-[#2D3142] leading-relaxed">
                  <p>
                    My dad (WA0PBQ) got his ham radio license in the 1960s and inspired me to get mine. I earned it in 1976 (WN0VEZ/WB0VEZ) and by 1978 I had my Amateur Extra ticket <strong>ADØK</strong> (20wpm CW). That sparked a lifelong interest in radio communications and electronics. Callsign <strong>ADØK</strong> can be heard on all the HF bands and on all the modes.
                  </p>
                  <p>
                    Don holds the coveted <strong>DXCC Award</strong> with 300+ entities confirmed — among other achievements — a testament to years of patient operating and a deep passion for chasing distant signals.
                  </p>
                  <p>
                    He is a proud member of the <a href="https://www.ctdxcc.org/" target="_blank" rel="noopener noreferrer" className="text-[#C5A059] hover:underline"><strong>Central Texas DX and Contesting Club</strong></a> in Austin, Texas, and the <a href="https://www.hchams.com/" target="_blank" rel="noopener noreferrer" className="text-[#C5A059] hover:underline"><strong>Hays-Caldwell County Amateur Radio Club</strong></a>.
                  </p>
                </div>
                <a
                  href="https://www.qrz.com/db/AD0K"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="qrz-link"
                  className="inline-flex items-center gap-2 mt-6 px-5 py-2.5 bg-[#0A1628] text-[#C5A059] text-sm font-semibold tracking-wide border border-[#0A1628] hover:bg-[#C5A059] hover:text-[#0A1628] transition-colors duration-200"
                >
                  <Signal size={14} />
                  View AD0K on QRZ.com
                </a>
              </div>

              {/* The Shack */}
              <div>
                <p className="overline mb-3">The Shack</p>
                <h2 className="font-serif text-3xl font-bold text-[#0A1628] mb-6">Equipment &amp; Capabilities</h2>
                <div className="space-y-3">
                  {[
                    { icon: Signal, label: "HF Transceiver",  value: "Kenwood TS-590SG" },
                    { icon: Wifi,   label: "Antenna System",  value: "Mosley TA-63N on 40 foot tower; 80/160m dipoles" },
                  ].map(({ icon: Icon, label, value }) => (
                    <div key={label} className="flex items-start gap-4 border border-[#0A1628]/12 p-4 bg-[#F5F0E8]">
                      <div className="w-8 h-8 bg-[#0A1628] flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Icon size={14} className="text-[#C5A059]" />
                      </div>
                      <div className="flex-1">
                        <div className="text-xs text-[#2D3142]/50 uppercase tracking-widest">{label}</div>
                        <div className="text-sm font-semibold text-[#0A1628]">{value}</div>
                      </div>
                    </div>
                  ))}
                  {/* DXCC Dashboard — third row */}
                  <Link
                    to="/ad0k/DXCC"
                    data-testid="dxcc-dashboard-link"
                    className="flex items-start gap-4 border border-[#0A1628]/12 p-4 bg-[#F5F0E8] hover:bg-[#C5A059]/10 transition-colors group"
                  >
                    <div className="w-8 h-8 bg-[#0A1628] flex items-center justify-center flex-shrink-0 mt-0.5 group-hover:bg-[#C5A059] transition-colors">
                      <BarChart2 size={14} className="text-[#C5A059] group-hover:text-[#0A1628] transition-colors" />
                    </div>
                    <div className="flex-1">
                      <div className="text-xs text-[#2D3142]/50 uppercase tracking-widest">DXCC Award</div>
                      <div className="text-sm font-semibold text-[#0A1628]">View DXCC Dashboard →</div>
                    </div>
                  </Link>
                </div>
              </div>

            </div>

            {/* ── RIGHT: Live Status + ClubLog ── */}
            <div className="space-y-8">

              {/* Live Station Status */}
              <div data-testid="station-status-section">
                <div className="flex items-center gap-2 mb-3">
                  <Activity size={14} className="text-[#0A1628]" />
                  <p className="overline text-xs">Live Station Status</p>
                </div>
                <div className="bg-[#1E212D] border border-[#C5A059]/30 font-mono">
                  <div className="flex items-center gap-2 px-4 py-2 border-b border-[#C5A059]/20">
                    <div className="flex gap-1">
                      <div className="w-2.5 h-2.5 rounded-full bg-red-500/60" />
                      <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/60" />
                      <div className="w-2.5 h-2.5 rounded-full bg-green-500/60" />
                    </div>
                    <span className="text-[#C5A059]/60 text-xs ml-1">ADØK — Station Monitor</span>
                    <div className="ml-auto flex items-center gap-3">
                      {/* Split indicator — only shown when on-air */}
                      {isOnline && (
                        <div data-testid="split-indicator" className={`flex items-center gap-1.5 px-2 py-0.5 border font-mono text-[10px] font-bold uppercase tracking-wider ${
                          status?.split
                            ? 'border-green-500/50 bg-green-500/10 text-green-400'
                            : 'border-red-500/30 bg-red-500/5 text-red-400/70'
                        }`}>
                          <div className={`w-1.5 h-1.5 rounded-full ${status?.split ? 'bg-green-400 animate-pulse' : 'bg-red-400/70'}`} />
                          Split
                        </div>
                      )}
                      {/* Online/Offline */}
                      <div className="flex items-center gap-1.5">
                        <div className={`w-1.5 h-1.5 rounded-full ${isOnline ? 'bg-green-400 animate-pulse' : 'bg-red-400'}`} />
                        <span className={`text-xs ${isOnline ? 'text-green-400' : 'text-red-400'}`}>
                          {isOnline ? 'ON AIR' : 'OFFLINE'}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="px-4 py-4">
                    {statusLoading ? (
                      <div className="flex items-center gap-2 text-[#C5A059]/60 text-xs">
                        <div className="w-3 h-3 border border-[#C5A059]/40 border-t-transparent rounded-full animate-spin" />
                        Querying...
                      </div>
                    ) : (
                      <div className="flex items-center gap-6">
                        <div data-testid="station-frequency">
                          <div className="text-[#C5A059]/50 text-xs uppercase tracking-widest mb-1">
                            {status?.split ? "RX" : "Frequency"}
                          </div>
                          <div className="text-green-400 text-xl font-bold terminal-glow">{status?.frequency || "--"}</div>
                        </div>
                        <div className="w-px h-10 bg-[#C5A059]/15" />
                        <div data-testid="station-mode">
                          <div className="text-[#C5A059]/50 text-xs uppercase tracking-widest mb-1">Mode</div>
                          <div className="text-[#C5A059] text-xl font-bold">
                            {status?.mode || "--"}
                            {(() => {
                              const ft8 = ["1.840","3.573","7.074","10.136","14.074","18.100","21.074","24.915","28.074","50.313"];
                              const ft4 = ["3.575","7.0475","10.140","14.080","18.104","21.140","24.919","28.180","50.318"];
                              const freq = status?.frequency || "";
                              if (ft8.some(f => freq.includes(f))) return <span className="ml-2 text-green-400 text-sm font-mono">· FT-8</span>;
                              if (ft4.some(f => freq.includes(f))) return <span className="ml-2 text-green-400 text-sm font-mono">· FT-4</span>;
                              return null;
                            })()}
                          </div>
                        </div>
                        {status?.split && (
                          <>
                            <div className="w-px h-10 bg-[#C5A059]/15" />
                            <div data-testid="station-tx-frequency">
                              <div className="text-[#C5A059]/50 text-xs uppercase tracking-widest mb-1">TX</div>
                              <div className="text-yellow-400 text-xl font-bold terminal-glow">
                                {status?.tx_frequency || "--"}
                              </div>
                            </div>
                          </>
                        )}
                        {lastFetched && (
                          <>
                            <div className="w-px h-10 bg-[#C5A059]/15" />
                            <div className="flex items-center gap-1 text-[#F5F0E8]/20 text-xs mt-4">
                              <Clock size={9} />
                              {lastFetched.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'UTC' })} Z
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <p className="text-[#2D3142]/30 text-xs mt-2 font-mono">Live via inbody.net. Refreshes every 2 minutes.</p>
              </div>

              {/* Club Log — QSO Activity widget (inline) */}
              <div data-testid="clublog-section">
                <div className="flex items-center gap-2 mb-3">
                  <Signal size={14} className="text-[#0A1628]" />
                  <p className="overline text-xs">Club Log — Last 20 QSOs</p>
                </div>
                {widgetHtml ? (
                  <iframe
                    data-testid="widget-iframe"
                    srcDoc={widgetHtml}
                    title="QSO Activity"
                    className="w-full border border-[#0A1628]/12"
                    style={{ minHeight: "720px", height: "100%" }}
                    sandbox="allow-scripts allow-same-origin"
                  />
                ) : (
                  <div className="border border-[#0A1628]/12 p-6 bg-[#F5F0E8] text-center">
                    <p className="text-xs text-[#2D3142]/40 font-mono">QSO activity not yet uploaded.</p>
                  </div>
                )}
              </div>

            </div>
          </div>
        </div>
      </section>

      {/* Propagation Analytics — full dashboard */}
      <section data-testid="propagation-section" className="py-10 bg-[#1E212D] border-t border-[#C5A059]/15">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">

          {/* Header */}
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-2">
              <Sun size={14} className="text-[#C5A059]" />
              <p className="overline text-xs text-[#C5A059]/70">Propagation Analytics</p>
            </div>
            {prop?.solar?.updated && (
              <span className="text-[#F5F0E8]/20 text-xs font-mono">{prop.solar.updated} UTC</span>
            )}
          </div>

          {propLoading ? (
            <div className="flex items-center gap-2 text-[#C5A059]/50 text-xs font-mono py-8">
              <div className="w-3 h-3 border border-[#C5A059]/40 border-t-transparent rounded-full animate-spin" />
              Fetching solar data...
            </div>
          ) : !prop ? (
            <p className="text-red-400/60 text-xs font-mono">Solar data unavailable.</p>
          ) : (
            <>
              {/* Geomagnetic alerts */}
              {prop.alerts?.length > 0 && (
                <div className="mb-5 space-y-2">
                  {prop.alerts.map((alert, i) => (
                    <div key={i} data-testid={`alert-${i}`}
                      className={`px-4 py-2 border text-xs font-mono flex items-start gap-3 ${
                        alert.type === "ALERT"
                          ? "border-red-500/30 bg-red-500/10 text-red-300"
                          : alert.type === "WARNING"
                          ? "border-orange-400/30 bg-orange-400/10 text-orange-300"
                          : "border-yellow-400/30 bg-yellow-400/10 text-yellow-300"
                      }`}>
                      <AlertTriangle size={12} className="mt-0.5 shrink-0" />
                      <span className="font-bold mr-1">{alert.type}:</span>
                      <span className="flex-1">{alert.summary}</span>
                      <span className="opacity-40 shrink-0">{alert.issued} UTC</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Gauges + Forecast */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">

                {/* Left: 3 gauge dials */}
                <div>
                  <p className="text-[#C5A059]/50 text-xs uppercase tracking-widest font-mono mb-3">Current Conditions</p>
                  <div className="grid grid-cols-3 gap-1">
                    <SolarGauge
                      value={prop.solar.sfi} min={60} max={250}
                      label="SFI" unit="Solar Flux"
                      zones={[
                        {from:60,  to:90,  color:"#ef4444"},
                        {from:90,  to:120, color:"#facc15"},
                        {from:120, to:180, color:"#4ade80"},
                        {from:180, to:250, color:"#22d3ee"},
                      ]}
                    />
                    <SolarGauge
                      value={prop.solar.kindex} min={0} max={9}
                      label="K-INDEX" unit="Geomagnetic"
                      zones={[
                        {from:0, to:3, color:"#4ade80"},
                        {from:3, to:5, color:"#facc15"},
                        {from:5, to:7, color:"#fb923c"},
                        {from:7, to:9, color:"#ef4444"},
                      ]}
                    />
                    <SolarGauge
                      value={prop.solar.aindex} min={0} max={50}
                      label="A-INDEX" unit="Geomagnetic"
                      zones={[
                        {from:0,  to:8,  color:"#4ade80"},
                        {from:8,  to:20, color:"#facc15"},
                        {from:20, to:40, color:"#fb923c"},
                        {from:40, to:50, color:"#ef4444"},
                      ]}
                    />
                  </div>
                </div>

                {/* Right: 27-day forecast */}
                <div className="flex flex-col">
                  <p className="text-[#C5A059]/50 text-xs uppercase tracking-widest font-mono mb-3">Forecast</p>
                  <div className="flex-1">
                    <ForecastChart data={prop.forecast} />
                  </div>
                </div>
              </div>

              {/* Secondary metrics row */}
              <div className="grid grid-cols-5 gap-2 mb-6 font-mono">
                {[
                  {label:"SUNSPOTS",  val: prop.solar.sunspots},
                  {label:"X-RAY",     val: prop.solar.xray},
                  {label:"S/N",       val: prop.solar.signalnoise},
                  {label:"WIND",      val: prop.solar.solarwind && `${prop.solar.solarwind} km/s`},
                  {label:"GEO FIELD", val: prop.solar.geomagfield},
                ].map(m => (
                  <div key={m.label} className="bg-[#0A1628]/60 border border-[#C5A059]/10 px-2 py-2 text-center">
                    <div className="text-[#C5A059]/40 text-xs uppercase tracking-widest mb-1">{m.label}</div>
                    <div className="text-[#F5F0E8] text-sm font-bold">{m.val || "N/A"}</div>
                  </div>
                ))}
              </div>

              {/* Band conditions */}
              <div>
                <p className="text-[#C5A059]/50 text-xs uppercase tracking-widest font-mono mb-3">Band Conditions</p>
                <div className="grid grid-cols-4 sm:grid-cols-8 gap-2 font-mono">
                  {prop.bands.map(b => {
                    const style = cs(b.condition);
                    return (
                      <div key={b.band} data-testid={`band-${b.band}`}
                        className="bg-[#0A1628]/60 border border-[#C5A059]/10 px-2 py-3 flex flex-col items-center gap-2">
                        <span className="text-[#C5A059] text-sm font-bold">{b.band}</span>
                        <div className={`w-2 h-2 rounded-full ${style.dot}`} />
                        <span className={`text-xs font-bold tracking-wide ${style.text}`}>{style.label}</span>
                        <span className="text-[#F5F0E8]/20 text-xs">{b.freq}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Legend */}
              <div className="flex flex-wrap items-center gap-4 mt-4 font-mono text-xs text-[#F5F0E8]/30">
                <span className="uppercase tracking-widest">{prop.time_key === "day" ? "Day" : "Night"} conditions</span>
                {[["bg-green-400","Good"],["bg-yellow-400","Fair"],["bg-red-500","Poor"],["bg-cyan-400","E-Skip"],["bg-slate-600","Closed"]].map(([dot,lbl]) => (
                  <span key={lbl} className="flex items-center gap-1">
                    <span className={`w-2 h-2 rounded-full inline-block ${dot}`} />{lbl}
                  </span>
                ))}
                <span className="ml-auto text-[#F5F0E8]/15">Sources: HamQSL/N0NBH · NOAA SWPC</span>
              </div>
            </>
          )}
        </div>
      </section>
      <Footer />
    </div>
  );
}
